<template>
  <div class="overview">
    <div class="overview__header">
      <h2 class="h4">Reservierter Termin</h2>
      <Timer />
    </div>
    <p class="overview__subtitle" v-if="showSubtitle">
      Bitte beachten Sie, dass der Termin nun für 10 Minuten reserviert ist. Sollten Sie den Termin
      in dieser Zeit nicht buchen, wird er wieder freigegeben.
    </p>
    <h3 class="h5 overview__subheader">Übersicht</h3>
    <div class="overview__table">
      <p @click="isMobile && jumpToStep(1)">Angebot</p>
      <button
        @click="jumpToStep(1)"
        class="overview__table__btn"
        :aria-label="`Angebot bearbeiten: ${selectedServicesName.join(', ')}`"
      >
        {{ selectedServicesName[0] }}
        {{ selectedServicesName.length > 1 ? ` • ${selectedServicesName[1]}` : '' }}
        <IconEdit size="20" :color="'#737380'" aria-hidden="true" />
      </button>
      <p @click="isMobile && jumpToStep(2)">Fahrzeug</p>
      <button
        @click="jumpToStep(2)"
        class="overview__table__btn"
        :aria-label="`Fahrzeug bearbeiten: ${chosenVehicle.brand} ${chosenVehicle.model}`"
      >
        {{
          chosenVehicle.brand +
          ' ' +
          chosenVehicle.model +
          (chosenVehicle.licensePlate ? `• ${chosenVehicle.licensePlate}` : '')
        }}
        <IconEdit size="20" :color="'#737380'" aria-hidden="true" />
      </button>
      <p @click="isMobile && jumpToStep(3)">Stützpunkt</p>
      <button
        @click="jumpToStep(3)"
        class="overview__table__btn"
        :aria-label="`Stützpunkt bearbeiten: ${activeStation.bezeichnung}, ${activeStation.adresse}, ${activeStation.plz} ${activeStation.ort}`"
      >
        {{
          activeStation.bezeichnung +
          '• ' +
          activeStation.adresse +
          ', ' +
          activeStation.plz +
          ' ' +
          activeStation.ort
        }}
        <IconEdit size="20" :color="'#737380'" aria-hidden="true" />
      </button>
      <p @click="isMobile && jumpToStep(4)">Termin</p>
      <button
        @click="jumpToStep(4)"
        class="overview__table__btn"
        :aria-label="`Termin bearbeiten: ${formatDateTime(
          'day, dateLong, timeFrom',
          lockedTimeslot && lockedTimeslot[0].von
        )}`"
      >
        {{
          formatDateTime(
            'day, dateLong • timeFrom, ca. ',
            lockedTimeslot && lockedTimeslot[0].von
          ) +
          getDuration(
            lockedTimeslot && lockedTimeslot[0].von,
            lockedTimeslot && (lockedTimeslot[1]?.bis || lockedTimeslot[0]?.bis)
          )
        }}
        <IconEdit size="20" :color="'#737380'" aria-hidden="true" />
      </button>
    </div>
    <h3 class="overview__subheader h5">Kosten</h3>
    <div class="overview__table">
      <template v-for="locked in lockedTimeslot" :key="locked.id">
        <p>{{ locked.snrBezeichnung }}</p>
        <p>
          <span>ca. </span>
          {{ locked.preis.toFixed(2).replace('.', ',') }}
          €
        </p>
      </template>
      <template v-if="user?.isMember && !userHasPaid">
        <p>Mitgliedsbeitrag</p>
        <p><span>ca. </span> {{ membershipFee.replace('.', ',') }} €</p>
      </template>
      <p>Gesamtkosten</p>
      <p><span>ca. </span> {{ totalCost().toFixed(2).replace('.', ',') }} €</p>
      <span class="overview__table__membershipfee" v-if="!user?.isMember">
        + ÖAMTC-Mitgliedsbeitrag</span
      >
    </div>
    <template
      v-if="buchungInternetText || membershipFeaturesText || membershipContractText.some((e) => e)"
    >
      <hr />
      <div class="overview__info">
        <h3 class="h5 overview__info__title">Wichtige Infos zu Ihrem Termin</h3>
        <p v-if="buchungInternetText">
          {{ buchungInternetText }}
        </p>
        <p v-if="membershipFeaturesText"><br />{{ membershipFeaturesText }}</p>
        <br />
        <p v-for="(text, index) in membershipContractText" :key="index">
          {{ text }}
        </p>
      </div>
    </template>
    <hr v-if="userHasPaid" />
    <Reminder :showHeader="false" />
    <h3 class="h4">Bitte überprüfen Sie Ihre Kontaktdaten</h3>
    <p class="overview__subtitle overview__subtitle--error" v-if="isPhoneRequired && !isPhoneSet">
      Bitte erfassen Sie eine Telefonnummer für ev. Rückfragen
    </p>
    <SelectionContact
      title="Telefon"
      name="Telefonnummer"
      :info="finalPhone"
      :divider="true"
      :opened="newPhone.opened"
      @toggle="newPhone.opened = $event"
    >
      <template v-slot:icon>
        <IconPhone aria-hidden="true" />
      </template>
      <template v-slot:expansion>
        <div class="overview__expansion">
          <el-checkbox v-model="newPhone.update" label="Mein ÖAMTC-Profil aktualisieren" />
          <MazPhoneNumberInput
            :aria-describedby="newPhone.error ? 'phone-error-message' : undefined"
            :aria-invalid="newPhone.error ? 'true' : undefined"
            v-model="newPhone.phone"
            label="Telefonnummer"
            autocomplete="tel"
            no-search
            no-example
            show-code-on-list
            default-country-code="AT"
            @update="validatePhone($event)"
            :translations="{
              countrySelector: {
                placeholder: '',
              },
              phoneInput: {
                placeholder: '',
              },
            }"
            :custom-countries-list="$options.germanCountryList"
          />
          <p
            id="phone-error-message"
            role="alert"
            aria-live="assertive"
            class="overview__expansion__input--error"
            v-if="newPhone.error"
          >
            Ungültige Eingabe
          </p>
          <button class="overview__expansion__save link" @click="changePhone">
            Änderung speichern
          </button>
          <hr />
        </div>
      </template>
    </SelectionContact>
    <SelectionContact
      title="E-Mail"
      name="E-Mail"
      :info="finalEmail"
      :opened="newEmail.opened"
      @toggle="newEmail.opened = $event"
    >
      <template v-slot:icon>
        <IconEmail aria-hidden="true" />
      </template>
      <template v-slot:expansion>
        <div class="overview__expansion">
          <el-checkbox v-model="newEmail.update" label="Mein ÖAMTC-Profil aktualisieren" />
          <el-input
            label="E-Mail Adresse"
            autocomplete="email"
            :aria-describedby="newEmail.error ? 'email-error-message' : undefined"
            :aria-invalid="newEmail.error ? 'true' : undefined"
            placeholder="E-Mail Adresse"
            v-model="newEmail.email"
            :class="newEmail.error && 'el-input--error'"
          />
          <p
            id="email-error-message"
            role="alert"
            aria-live="assertive"
            class="overview__expansion__input--error"
            v-if="newEmail.error"
          >
            {{ newEmail.error }}
          </p>
          <button class="overview__expansion__save link" @click="changeEmail">
            Änderung speichern
          </button>
        </div>
      </template>
    </SelectionContact>
    <Navigation
      :nextLabel="previousAppointmentId ? 'Änderungen Speichern' : 'Verbindlich buchen'"
      :isNextButtonDisabled="!isValidContact"
      @nextStep="$event === 5 && updateUserDetails()"
    />
  </div>
</template>

<script>
import { germanCountryList, phoneEmtpyText, phoneRequiredSNR } from '@/utils/constants'
import { formatDateTime, getDuration } from '@/utils/format.js'
import { trackGa4Event } from '@/utils/tracking'
import MazPhoneNumberInput from 'maz-ui/components/MazPhoneNumberInput'
import { mapGetters } from 'vuex'

import IconEdit from '@/components/icons/IconEdit.vue'
import IconEmail from '@/components/icons/IconEmail.vue'
import IconPhone from '@/components/icons/IconPhone.vue'

import Navigation from '@/components/Booking/Navigation.vue'
import Timer from '@/components/Booking/Timer.vue'
import Reminder from '@/components/Home/Reminder.vue'
import SelectionContact from '@/components/SelectionContact.vue'

export default {
  components: {
    MazPhoneNumberInput,
    IconEdit,
    IconPhone,
    IconEmail,
    Reminder,
    Timer,
    Navigation,
    SelectionContact,
  },
  germanCountryList,
  data() {
    return {
      showSubtitle: true,
      newPhone: {
        update: false,
        phone: '',
        error: false,
        opened: false,
      },
      newEmail: {
        update: false,
        email: '',
        error: false,
        opened: false,
      },
    }
  },
  mounted() {
    this.populateDataFromStore()
    this.$store.dispatch('checkMembershipFeatures')

    if (this.user) {
      this.$store.commit('setFinalEmail', this.user?.email)

      if (this.userAccountInfo?.phone) {
        this.$store.commit('setFinalPhone', this.userAccountInfo.phone)
      }
    }
  },
  activated() {
    // User lands on the fifth step of the TVG
    trackGa4Event({
      ga4Event: 'tvg_step_5',
      ga4ParameterName: 'region',
      ga4ParameterValue: this.selectedState.bezeichnung,
    })
  },
  computed: {
    ...mapGetters([
      'isMobile',
      'user',
			'userHasPaid',
      'membershipFee',
      'membershipFeaturesText',
      'membershipContractText',
      'userAccountInfo',
      'userEmailError',
      'finalPhone',
      'finalEmail',
      'savedPhone',
      'savedEmail',
      'selectedState',
      'selectedServicesId',
      'selectedServicesName',
      'selectedVehicle',
      'newVehicle',
      'activeStation',
      'buchungInternetText',
      'lockedTimeslot',
      'previousAppointmentId',
    ]),
    chosenVehicle() {
      return this.selectedVehicle ? this.selectedVehicle : this.newVehicle
    },
    isPhoneChanged() {
      if (this.userAccountInfo?.phone) {
        return this.finalPhone !== this.userAccountInfo.phone
      } else {
        return /\d/.test(this.finalPhone) // true if has digit
      }
    },
    isEmailChanged() {
      return this.finalEmail !== this.user?.email
    },
    isValidContact() {
      if (this.isPhoneRequired) {
        return this.isPhoneSet && this.finalEmail
      }
      return true
    },
    isPhoneRequired() {
      return this.selectedServicesId.some((id) => id === phoneRequiredSNR)
    },
    isPhoneSet() {
      return this.finalPhone !== phoneEmtpyText
    },
  },
  watch: {
    newPhone: {
      deep: true,
      handler() {
        this.$store.commit('setSavedPhone', this.newPhone)
      },
    },
    newEmail: {
      deep: true,
      handler() {
        this.$store.commit('setSavedEmail', this.newEmail)
      },
    },
    'newPhone.phone'() {
      // when clearing input
      if (!this.newPhone.phone) this.newPhone.error = false
    },
    userEmailError() {
      if (this.userEmailError) {
        this.newEmail.error = this.userEmailError
        this.newEmail.opened = true
      } else if (this.userEmailError === false) {
        this.saveAppointment()
      }
    },
  },
  methods: {
    formatDateTime,
    getDuration,
    populateDataFromStore() {
      this.newPhone = this.savedPhone
      this.newEmail = this.savedEmail
    },
    jumpToStep(step) {
      this.$store.commit('setActiveStep', step)
      this.$store.dispatch('deleteLockedAppointment')
    },
    totalCost() {
      const servicesCost = this.lockedTimeslot?.reduce(
        (prevValue, currentValue) => prevValue + currentValue.preis,
        0
      )

      return servicesCost + +this.membershipFee
    },
    validatePhone(e) {
      this.newPhone.error = !e.isValid
    },
    changePhone() {
      if (!this.newPhone.error) {
        this.newPhone.opened = false // close accordion
        this.$store.commit('setFinalPhone', this.newPhone.phone)
      }
    },
    changeEmail() {
      if (
        // validate on save
        !this.newEmail.email.includes('@') ||
        !this.newEmail.email.includes('.')
      ) {
        this.newEmail.error = 'Ungültige E-mail'
      } else {
        this.newEmail.error = false
        this.newEmail.opened = false // close accordion
        this.$store.commit('setFinalEmail', this.newEmail.email)
      }
    },
    updateUserDetails() {
      if (this.isPhoneChanged && this.newPhone.update)
        this.$store.dispatch('updateUserPhone', this.finalPhone)
      if (this.isEmailChanged && this.newEmail.update) {
        this.$store.dispatch('updateUserEmail', this.finalEmail)
        return // proceed to error check
      }

      this.saveAppointment()
    },
    async saveAppointment() {
      if (this.isPhoneChanged || this.isEmailChanged) {
        this.$store.dispatch('updateAppointmentContact', {
          phone: this.finalPhone,
          email: this.finalEmail,
        })
      }

      await this.$store.dispatch('confirmAppointment')
      await this.$store.dispatch('confirmAppointmentToUser', {
        phone: this.finalPhone,
        email: this.finalEmail,
      })


      // User books the appointment
      trackGa4Event({
        ga4Event: 'tvg_book_appointment',
        ga4ParameterName: 'value',
        ga4ParameterValue: this.totalCost(),
      })

      if (this.previousAppointmentId)
        this.$store.dispatch('deleteAppointment', this.previousAppointmentId)

			this.$store.commit('clearLockedTimeslot')

      this.$store.commit('setActiveStep', 0)
    },
  },
}
</script>

<style lang="scss">
.overview {
  .h4 {
    margin-bottom: 4px;

    &:last-of-type {
      margin-bottom: 16px;
      + p {
        margin-top: -12px;
        margin-bottom: 16px;
      }
    }
  }

  &__header {
    display: flex;
    align-items: baseline;
  }

  &__subtitle {
    @include subtitle;

    &--error {
      color: $color-danger-primary;
    }
  }

  &__subheader {
    font-weight: $font-weight-bold;
    margin-top: 32px;
    margin-bottom: 6px;

    @media (min-width: $breakpoint-mobile) {
      margin-top: 24px;
    }
  }

  &__table {
    > :nth-child(odd) {
      color: $color-text-secondary;
      font-size: 12px;
      line-height: 1.75;
      padding-top: 8px;
    }

    > :nth-child(even) {
      position: relative;
      padding: 0 0 8px 0;
      display: flex;
      width: 100%;

      svg {
        position: absolute;
        right: 0;
        bottom: 20px;
      }

      &::after {
        content: '';
        position: absolute;
        left: -16px;
        bottom: 0;
        width: calc(100% + 32px);
        border-bottom: 1px solid $color-grey-light;
      }
    }

    &__btn {
      text-align: start;
      cursor: pointer;
      > * {
        flex-shrink: 0;
      }
    }

    ~ .overview__table {
      display: inline-grid;
      grid-template-columns: auto max-content;
      row-gap: 5px;
      align-items: center;
      position: relative;
      width: 100%;

      > :nth-child(odd) {
        font-size: 16px;
        padding-top: 0;
      }

      > :nth-child(even) {
        justify-self: flex-end;
        padding-bottom: 0;
        justify-content: flex-end;

        &::after {
          content: '';
          border-bottom: none;
        }
      }

      > :nth-last-child(2),
      > :last-child {
        color: $color-text-primary;
        font-weight: $font-weight-medium;
      }

      .overview__table__membershipfee {
        font-weight: $font-weight-medium;
        position: absolute;
        right: 0;
        bottom: -16px;
      }
    }

    @media (min-width: $breakpoint-mobile) {
      display: inline-grid;
      grid-template-columns: minmax(min-content, 140px) 1fr;
      row-gap: 5px;

      :nth-child(odd) {
        padding-top: 0;
        cursor: default;
      }

      .overview__table__btn,
      p:nth-of-type(even) {
        padding-bottom: 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        column-gap: 8px;

        svg {
          position: static;
          visibility: visible; // Always show icons
        }

        &:hover,
        &:focus {
          text-decoration: underline;
          outline: none;
        }

        &::after {
          content: '';
          border-bottom: none;
        }
      }

      ~ .overview__table {
        grid-template-columns: minmax(min-content, 140px) 1fr;
        width: auto;

        p:nth-of-type(odd) {
          font-size: 12px;
        }

        p:nth-of-type(even) {
          display: flex;
          gap: 4px;
          justify-content: space-between;
          width: 100%;

          &:hover {
            cursor: default;
            text-decoration: none;
          }
        }

        .overview__table__membershipfee {
          position: absolute;
          right: -180px;
          bottom: 0;
        }
      }
    }
  }

  &__info {
    &__title {
      font-weight: $font-weight-bold;
      margin-bottom: 4px;
    }

    p:not(.overview__info__title) {
      color: $color-text-secondary;
      white-space: pre-line;

      &:last-of-type {
        margin-bottom: 24px;
      }
    }
  }

  > hr {
    @include container-divider;
  }

  &__expansion {
    display: flex;
    flex-direction: column;
    margin-top: 6px;
    margin-left: 56px;

    .el-checkbox {
      margin: 0 -8px 16px;
      padding: 4px 8px;

      &__label {
        color: $color-text-secondary;
      }

      &:hover {
        background-color: none;
      }
    }

    .m-phone-number-input {
      max-width: 300px;

      &__country-flag {
        bottom: auto;
        top: 50%;
        transform: translateY(-50%);
      }

      &__select {
        width: 8rem;

        .m-input-wrapper {
          border: 1px solid $color-grey-darker;
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;

          &-input {
            width: 3rem;
          }
        }

        &__item {
          font-size: 16px;

          span:nth-of-type(2) {
            margin-right: 0.5rem;
          }
        }

        .m-select-list {
          border-radius: 4px;
          max-width: 300px !important;

          &-item {
            &:hover {
              background-color: rgba($color-grey-light, 0.3);
            }
            &.--is-selected {
              &.--primary {
                background-color: $color-info-primary;
              }

              span {
                background-color: $color-info-primary;
                color: white;
              }
            }
          }
        }
      }

      &__input {
        .m-input-wrapper {
          border: 1px solid $color-grey-darker;
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;

          &.maz-border-danger {
            border: 1px solid $color-danger-primary;
          }
        }
      }

      .maz-border-primary {
        border-color: $color-info-primary;
      }

      .m-input.--md {
        height: 44px;
      }
    }

    .el-input {
      max-width: 300px;

      &--error .el-input__inner {
        border-color: $color-danger-primary;
      }
    }

    &__input--error {
      color: $color-danger-primary;
      font-size: 12px;
      margin-top: 4px;
    }

    &__save {
      margin-top: 16px;
      margin-bottom: 24px;
      align-self: flex-start;
    }
  }

  .navigation {
    margin-top: 40px;
  }
}
</style>
